import { useRouter } from 'next/router'
import React from 'react'

import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { DEFAULT_CURRENCY } from '@/lib/constants'
import { DeviceOrderData } from '@/lib/schemes'
import { Device, OrderEstimate } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import { useCurrencySymbol } from '@/hooks/useCurrencySymbol'

import CountInput from '@/components/CountInput'
import Icon from '@/components/Icon'
import Label from '@/components/Label'
import Rule from '@/components/Rule'
import FormField from '@/components/form/FormField'
import RowSkeleton from '@/components/skeletons/RowSkeleton'

const USD_STEP = 5
const RUB_STEP = 500

export const OrderAdvanceBlock = ({
  form,
  device,
  estimate,
  isLoading
}: {
  form: UseFormReturn<DeviceOrderData>
  device: Device
  estimate?: OrderEstimate
  isLoading: boolean
}) => {
  const { locale } = useRouter()
  const { symbol } = useCurrencySymbol()

  const { currency: deviceCurrency } = device
  const { prepaidDays, minAdvance: minAdvanceEstimate } = estimate || {}
  const {
    watch,
    register,
    formState: { errors }
  } = form

  const currency = deviceCurrency || DEFAULT_CURRENCY
  const isUSD = currency === 'USD'
  const advance = watch('advance')

  const hasPrepaidDays =
    advance &&
    minAdvanceEstimate !== undefined &&
    advance * 100 >= minAdvanceEstimate

  return (
    <div className="flex flex-col">
      <Rule className="mb-5 w-full border-slate-300" />
      <div className="mb-2 flex gap-4 tablet:mb-3 tablet:flex-row">
        <FormField
          className="w-full gap-2.5"
          error={errors.advance}
          values={{
            minAdvance: formatCurrency(
              minAdvanceEstimate || 0,
              currency,
              locale
            )
          }}
        >
          <Label>
            <FormattedMessage
              {...{
                id: 'device.hosting.prepayment',
                defaultMessage: 'Размер предоплаты за хостинг'
              }}
            />
          </Label>
          <FormField className="tablet:max-w-[263px]">
            <CountInput
              {...register('advance')}
              disabled={watch('paymentMethod') === 'balance'}
              min={minAdvanceEstimate && minAdvanceEstimate / 100}
              step={isUSD ? USD_STEP : RUB_STEP}
              suffix={symbol}
              value={advance as number}
            />
          </FormField>
        </FormField>
      </div>
      {!!hasPrepaidDays && (
        <div className="flex gap-2">
          <Icon
            className="h-4 w-4 flex-none text-amber-500"
            name="exclamation-circle-16"
          />
          {isLoading ? (
            <RowSkeleton className="h-[18px] w-64" />
          ) : (
            <p className="text-x-sm leading-x-sm tablet:w-full">
              <FormattedMessage
                {...{
                  id: 'dialog.device.purchase.amountDue',
                  defaultMessage:
                    'Предоплаты хватит на {days} дней, далее вы сможете пополнить баланс',
                  values: {
                    days: !!prepaidDays ? prepaidDays : 0
                  }
                }}
              />
            </p>
          )}
        </div>
      )}
    </div>
  )
}
